import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Camera, Settings, LogOut, LayoutDashboard, BarChart3 } from 'lucide-react';
import { AlertTriangle, Clock, Smartphone, Heart, ShieldAlert, Wifi, Video, Film, FolderOpen, Fingerprint, Users, Power } from 'lucide-react';
import { Library } from 'lucide-react';

// `perm` es el permiso que hace visible cada entrada. Una seccion sin ninguna
// entrada visible desaparece entera (titulo incluido). Esconder el menu es solo
// cosmetica: quien pegue la URL a mano llega a la pagina, pero sus llamadas a
// la API se topan con el 403. La autorizacion de verdad esta en el backend.
const SECTIONS = [
  {
    title: 'Operación',
    items: [
      { to: '/', icon: LayoutDashboard, label: 'Dashboard', exact: true, perm: 'devices.read' },
      { to: '/live', icon: Video, label: 'Video en vivo', perm: 'live.view' },
      { to: '/recordings', icon: Film, label: 'Grabaciones', perm: 'recordings.read' },
      { to: '/videos', icon: FolderOpen, label: 'Videos descargados', perm: 'recordings.read' },
    ],
  },
  {
    title: 'Monitoreo',
    items: [
      { to: '/stats', icon: BarChart3, label: 'Consumo de Datos', perm: 'stats.read' },
      { to: '/health', icon: Heart, label: 'Salud', perm: 'health.read' },
      { to: '/capturas', icon: Camera, label: 'Capturas', perm: 'snapshots.read' },
      { to: '/alerts', icon: AlertTriangle, label: 'Alertas', perm: 'alerts.read' },
    ],
  },
  {
    title: 'Configuración',
    items: [
      { to: '/config', icon: Settings, label: 'Configuración', perm: 'config.read' },
      { to: '/panel', icon: ShieldAlert, label: 'Panel Alarma', perm: 'panel.read' },
      { to: '/accesos', icon: Fingerprint, label: 'Control de Acceso', perm: 'access.read' },
      { to: '/mdvr-wifi', icon: Wifi, label: 'WiFi MDVR', perm: 'mdvr.read' },
      { to: '/apagado', icon: Power, label: 'Apagado MDVR', perm: 'mdvr.read' },
      { to: '/vms', icon: Video, label: 'Cámaras', perm: 'vms.read' },
      { to: '/intervals', icon: Clock, label: 'Intervalos', perm: 'config.read' },
      { to: '/samsara', icon: Smartphone, label: 'Samsara', perm: 'config.read' },
      { to: '/library', icon: Library, label: 'Biblioteca', perm: 'library.read' },
    ],
  },
  {
    title: 'Administración',
    items: [
      { to: '/usuarios', icon: Users, label: 'Usuarios y Roles', perm: 'users.read' },
    ],
  },
];

const Layout = () => {
  const { user, logout, can } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => { logout(); navigate('/login'); };

  // Sin la migracion 0009 aplicada no hay roles que mostrar; antes aqui decia
  // "Operador" fijo para todo el mundo, que era mentira la mitad de las veces.
  const rolesTexto = (user?.roles?.length ? user.roles.join(' · ') : 'Sin rol');

  const isActive = (to, exact) =>
    exact ? location.pathname === to : location.pathname === to || location.pathname.startsWith(to + '/');

  return (
    <div style={styles.container}>
      <aside style={styles.sidebar}>
        <div style={styles.logo}>
          <div style={styles.logoMark}><Camera size={20} /></div>
          <div>
            <div style={styles.logoText}>HikSnap</div>
            <div style={styles.logoSub}>Fletes Sur · v2</div>
          </div>
        </div>

        <nav style={styles.nav}>
          {SECTIONS.map(sec => {
            const items = sec.items.filter(it => !it.perm || can(it.perm));
            if (items.length === 0) return null;
            return (
              <div key={sec.title} style={styles.section}>
                <div style={styles.sectionTitle}>{sec.title}</div>
                {items.map(it => (
                  <NavLink key={it.to} to={it.to} Icon={it.icon} label={it.label}
                           active={isActive(it.to, it.exact)} />
                ))}
              </div>
            );
          })}
        </nav>

        <div style={styles.userSection}>
          <div style={styles.user}>
            <div style={styles.avatar}>{user?.username?.[0]?.toUpperCase()}</div>
            <div style={{ overflow: 'hidden' }}>
              <div style={styles.userName}>{user?.full_name || user?.username}</div>
              <div style={styles.userRole} title={rolesTexto}>
                {rolesTexto}
                {user?.all_devices === false && ` · ${user.devices?.length || 0} equipos`}
              </div>
            </div>
          </div>
          <button onClick={handleLogout} style={styles.logoutBtn} title="Cerrar sesión"><LogOut size={18} /></button>
        </div>
      </aside>
      <main style={styles.main}><Outlet /></main>
    </div>
  );
};

const NavLink = ({ to, Icon, label, active }) => {
  const [hover, setHover] = useState(false);
  const style = {
    ...styles.navLink,
    ...(active ? styles.navLinkActive : {}),
    ...(!active && hover ? styles.navLinkHover : {}),
  };
  return (
    <Link to={to} style={style} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <span style={{ ...styles.activeBar, opacity: active ? 1 : 0 }} />
      <Icon size={18} />
      <span>{label}</span>
    </Link>
  );
};

const styles = {
  container: { display: 'flex', minHeight: '100vh', background: '#f1f5f9' },
  sidebar: { width: '244px', background: 'linear-gradient(180deg,#0f172a,#111827)', color: 'white', display: 'flex', flexDirection: 'column', position: 'fixed', height: '100vh', borderRight: '1px solid rgba(148,163,184,.12)' },
  logo: { padding: '18px 18px', display: 'flex', alignItems: 'center', gap: '12px', borderBottom: '1px solid rgba(148,163,184,.12)' },
  logoMark: { width: 38, height: 38, borderRadius: 11, background: 'linear-gradient(135deg,#2563eb,#7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 6px 16px rgba(37,99,235,.35)' },
  logoText: { fontSize: 17, fontWeight: 800, letterSpacing: .2 },
  logoSub: { fontSize: 11, color: '#94a3b8', marginTop: 1 },
  nav: { flex: 1, padding: '12px 12px', display: 'flex', flexDirection: 'column', gap: '4px', overflowY: 'auto' },
  section: { marginBottom: 10 },
  sectionTitle: { fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#64748b', padding: '8px 12px 4px' },
  navLink: { position: 'relative', display: 'flex', alignItems: 'center', gap: '11px', padding: '10px 14px', borderRadius: '9px', color: '#cbd5e1', textDecoration: 'none', fontSize: 13.5, fontWeight: 500, transition: 'background .12s ease, color .12s ease' },
  navLinkHover: { background: 'rgba(148,163,184,.10)', color: '#fff' },
  navLinkActive: { background: 'linear-gradient(90deg,rgba(37,99,235,.22),rgba(37,99,235,.05))', color: '#fff', fontWeight: 700 },
  activeBar: { position: 'absolute', left: 0, top: 8, bottom: 8, width: 3, borderRadius: 3, background: '#3b82f6', transition: 'opacity .12s ease' },
  userSection: { padding: '14px', borderTop: '1px solid rgba(148,163,184,.12)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  user: { display: 'flex', alignItems: 'center', gap: '10px', overflow: 'hidden' },
  avatar: { width: '36px', height: '36px', borderRadius: '50%', background: 'linear-gradient(135deg,#2563eb,#7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', flexShrink: 0 },
  userName: { fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  userRole: { fontSize: 11, color: '#94a3b8' },
  logoutBtn: { background: 'rgba(148,163,184,.10)', border: 'none', color: '#cbd5e1', cursor: 'pointer', padding: '9px', borderRadius: '8px', display: 'flex', flexShrink: 0 },
  main: { flex: 1, marginLeft: '244px', minHeight: '100vh' },
};

export default Layout;
